import { KeyboardEvent, PointerEvent, useState, useRef, useMemo, useEffect, useLayoutEffect } from "react";
import { Subscription, fromEvent, map, switchMap, tap, takeUntil } from "rxjs";
import { createVideoClip } from "src/api/video-clips.api";
import { usePlayer, usePlayerUpdates, PlaybackStatuses } from "src/lib/kaltura-player";
import { getEntryIdFromSource } from "src/lib/utils/entry-id-from-source";
import { getTimeStampParts } from "src/lib/utils/timestamp-parts";
import { SubtitleOption } from "src/models/subtitle-option.model";

export function useVideoClip(
  source: string,
  subtitle_option: SubtitleOption,
  handleCreateClip: (videoUrl: string, subtitleUrl: string) => Promise<void>
) {
  const [message, setMessage] = useState("");
  const [messageStatusClassname, setMessageStatusClassname] = useState("bg-black/90")
  const [disabled, setDisabled] = useState(false);
  const errorTimeout = useRef<NodeJS.Timeout | null>(null);

  const [playerId, setPlayerId] = useState("");
  const [playerPlaying, setPlayerPlaying] = useState(false);
  const playerDuration = useRef(0);
  const { playerPlay, playerPause, playerSeek } = usePlayer(playerId);
  const { playerState$, playerDuration$, playerTime$ } = usePlayerUpdates(playerId);

  const [startTime, setStartTime] = useState(0);
  const [startLeft, setStartLeft] = useState(0);
  const [start, setStart] = useState("00:00:00");
  const startRef = useRef<HTMLButtonElement>(null);

  const [endTime, setEndTime] = useState(0);
  const [endLeft, setEndLeft] = useState(1);
  const [end, setEnd] = useState("00:00:00");
  const endRef = useRef<HTMLButtonElement>(null);

  const clipRange = useRef<HTMLDivElement>(null);

  const [currentTime, setCurrentTime] = useState(0);

  const entryId = useMemo(() => getEntryIdFromSource(source), [source]);

  useEffect(() => {
    if (!playerId) {
      return;
    }
    const stateSubscription = playerState$.subscribe((result) => {
      setPlayerPlaying(result === PlaybackStatuses.Playing);
    });

    const durationSubscription = playerDuration$.subscribe((result) => {
      if (result) {
        playerPause();
      }
      setEndTime(result);
      setEnd(getPosTime(endLeft, result));
      playerDuration.current = result;
    });

    return () => {
      stateSubscription.unsubscribe();
      durationSubscription.unsubscribe();
    };
  }, [playerId, playerState$, playerDuration$]);

  useEffect(() => {
    if (!playerId) {
      return;
    }

    const timeSubscription = playerTime$.subscribe((result) => {
      setCurrentTime(result);
      if (result >= endTime) {
        playerPause();
      }
    });

    return () => timeSubscription.unsubscribe();
  }, [playerId, playerTime$, endTime]);

  useLayoutEffect(() => {
    let startDragSubscription: Subscription;
    let endDragSubscription: Subscription;

    if (startRef.current) {
      startDragSubscription = fromEvent<PointerEvent<HTMLButtonElement>>(startRef.current, "pointerdown")
        .pipe(
          map((e: PointerEvent<HTMLButtonElement>) => {
            const { left } = startRef.current!.getBoundingClientRect();
            const offset = e.clientX - left;
            return offset;
          }),
          switchMap((initialOffset: number) =>
            fromEvent<PointerEvent<HTMLButtonElement>>(document, "pointermove").pipe(
              tap((e: PointerEvent<HTMLButtonElement>) => {
                const { left, width } = clipRange.current!.getBoundingClientRect();
                const offset = (e.clientX + initialOffset - left) / width;
                const maxOffset = parseFloat(endRef.current!.style.left) / 100;
                const percentage = Math.min(Math.max(0, offset), maxOffset);
                const time = Math.floor(playerDuration.current * percentage);
                setStartLeft(percentage);
                setStart(getPosTime(percentage, playerDuration.current));
                playerSeek({ seekTo: time, pause: true });
                setStartTime(time);
              }),
              takeUntil(fromEvent<PointerEvent<HTMLButtonElement>>(document, "pointerup"))
            )
          )
        )
        .subscribe();
    }
    if (endRef.current) {
      endDragSubscription = fromEvent<PointerEvent<HTMLButtonElement>>(endRef.current, "pointerdown")
        .pipe(
          map((e: PointerEvent<HTMLButtonElement>) => {
            const { left } = endRef.current!.getBoundingClientRect();
            const offset = e.clientX - left;
            return offset;
          }),
          switchMap((initialOffset: number) =>
            fromEvent<PointerEvent<HTMLButtonElement>>(document, "pointermove").pipe(
              tap((e: PointerEvent<HTMLButtonElement>) => {
                const { left, width } = clipRange.current!.getBoundingClientRect();
                const offset = (e.clientX - initialOffset - left) / width;
                const minOffset = parseFloat(startRef.current!.style.left) / 100;
                const percentage = Math.max(Math.min(offset, 1), minOffset);
                const time = Math.ceil(playerDuration.current * percentage);
                setEndLeft(percentage);
                setEnd(getPosTime(percentage, playerDuration.current));
                setEndTime(time);
              }),
              takeUntil(fromEvent<PointerEvent<HTMLButtonElement>>(document, "pointerup"))
            )
          )
        )
        .subscribe();
    }

    return () => {
      if (errorTimeout.current) {
        clearTimeout(errorTimeout.current);
      }
      startDragSubscription?.unsubscribe();
      endDragSubscription?.unsubscribe();
    };
  }, []);

  const handlePlayerLoaded = (data: { playerId: string }) => {
    const { playerId } = data;
    if (!playerId) {
      return;
    }
    setPlayerId(playerId);
  };

  const handleToStart = () => {
    playerSeek({ seekTo: startTime, pause: true });
  };

  const handleToEnd = () => {
    playerSeek({ seekTo: endTime, pause: true });
  };

  const handleStartKeydown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const validKeys = ["Left", "ArrowLeft", "Right", "ArrowRight"];
    if (!validKeys.includes(e.key)) {
      return;
    }
    e.preventDefault();
    const offset = e.shiftKey ? 10_000 : 1_000;
    const time = e.key.includes("Left") ? Math.max(startTime - offset, 0) : Math.min(startTime + offset, endTime);
    handleStartTimeChange(time);
  };

  const handleEndKeydown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const validKeys = ["Left", "ArrowLeft", "Right", "ArrowRight"];
    if (!validKeys.includes(e.key)) {
      return;
    }
    e.preventDefault();
    const offset = e.shiftKey ? 10_000 : 1_000;
    const time = e.key.includes("Left")
      ? Math.max(endTime - offset, startTime)
      : Math.min(endTime + offset, playerDuration.current);
    handleEndTimeChange(time);
  };

  const handleStartTimeChange = (time: number) => {
    const percentage = time / playerDuration.current;
    setStartTime(time);
    setStartLeft(percentage);
    setStart(getPosTime(percentage, playerDuration.current));
    playerSeek({ seekTo: time, pause: true });
  };

  const handleEndTimeChange = (time: number) => {
    const percentage = time / playerDuration.current;
    setEndTime(time);
    setEndLeft(percentage);
    setEnd(getPosTime(percentage, playerDuration.current));
  };

  const checkBlobExists = async (videoUrl: string, subtitleUrl: string): Promise<void> => {
    while(true) {
      try {
        const response = await fetch(videoUrl, { method: 'HEAD' });

        if (response.ok) {
          await handleCreateClip(videoUrl, subtitleUrl);
          break
        }
      } catch (error) {
        console.error('Error checking blob existence:', error);
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
  }

  const createClip = async () => {
    try {
      if (errorTimeout.current) {
        clearTimeout(errorTimeout.current);
      }
      setMessage("Der Clip wird erstellt und steht gleich zur Verfügung.");
      setDisabled(true);
      const { videoUrl, subtitleUrl } = await createVideoClip({
        source,
        start,
        end,
        subtitle_option,
      });

      await checkBlobExists(videoUrl, subtitleUrl).then(() => console.log("Video clip is ready to download.")).catch((error) => console.error("Error checking blob", error))
      setMessage("Ihr Video wurde erfolgreich erstellt und heruntergeladen.");
      setMessageStatusClassname("bg-green-800");
    } catch (error: any) {
      setMessage(error.message);
      setMessageStatusClassname("bg-red-100");
      errorTimeout.current = setTimeout(() => setMessage(""), 5_000);
    } finally {
      setDisabled(false);
    }
  };

  return {
    entryId,
    handlePlayerLoaded,
    message,
    messageStatusClassname,
    startTime,
    startLeft,
    startRef,
    handleStartTimeChange,
    handleStartKeydown,
    handleToStart,
    endTime,
    endLeft,
    endRef,
    handleEndTimeChange,
    handleEndKeydown,
    handleToEnd,
    clipRange,
    currentTime,
    playerPlaying,
    playerPlay,
    playerPause,
    playerDuration,
    disabled,
    createClip,
  };
}

function getPosTime(percentage: number, duration: number) {
  if (!duration) {
    return "00:00:00";
  }
  let value = Math.floor(duration * percentage);
  const { h, m, s } = getTimeStampParts(value);
  return `${h}:${m}:${s}`;
}
